import { PageHeader } from "@/components/common/PageHeader";
import { Alert, AlertTitle, Box, Button, IconButton, Tooltip } from "@mui/material";
import { Add as AddIcon, CloudUpload as UploadIcon } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { Tab, useRecordContext, useGetList, useGetOne } from "react-admin";
import { useLocation } from "react-router-dom";
import { ClaimsSummaryTab } from "./components/claims-summary/ClaimsSummaryTab";
import { AggregateDeductibleTab } from "./components/aggregate-deductible/AggregateDeductibleTab";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { ClientSideDrawer } from "./components/ClientSideDrawer";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { getResourceIcon } from "@/utils/icons";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { ClientCommentsButton } from "./components/ClientCommentsButton";
import { PageContent } from "@/components/common/PageLayout";
import { ClientOverviewTab } from "./components/ClientOverviewTab";
import { ClientReportingTab } from "./components/ClientReportingTab";
import { ClientClaimsTab } from "../../components/common/ClaimsTab";
import { AlertType, Client, ClientStatus, Policy, PolicyStatus, ResourceName, type Vendor } from "@/types";
import { buildPolicyVendorContext } from "@/components/common/DocumentDropzone/policyVendorSelector";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ViewTasksButton } from "../tasks/components/ViewTasksButton";
import { PolicyProvider, usePolicyContext } from "../claimants/components/PolicyContext";
import { PolicySelector } from "../claimants/components/PolicySelector";
import { featureFlags } from "@/utils/featureFlags";
import { UploadTypeDialog } from "@/pages/landing/components/UploadTypeDialog";
import { PolicyCreateDialog } from "../policies/components/PolicyCreateDialog";

export const ClientShow = () => {
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const toggleDrawer = () => setDrawerExpanded((prev) => !prev);

  const ClientIcon = getResourceIcon(ResourceName.CLIENTS);

  return (
    <StyledShow actions={false}>
      <ClientShowInner
        drawerExpanded={drawerExpanded}
        setDrawerExpanded={setDrawerExpanded}
        toggleDrawer={toggleDrawer}
        ClientIcon={ClientIcon}
      />
    </StyledShow>
  );
};

/**
 * Client-page upload dialog. Rendered below PolicyProvider so the claims vendor
 * context keys off the selected policy — or the UNION of the client's active
 * policies when "All Policies" is selected — rather than one arbitrarily-chosen
 * policy. This puts the "0 or 2+ primaries → don't guess" rule at the client grain,
 * so a multi-administrator client never gets a confidently-wrong prefill (#5591).
 */
const ClientUploadDialog = ({
  open,
  onClose,
  clientId,
  clientLabel,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string | undefined;
  clientLabel: string | undefined;
}) => {
  const { policies, selectedPolicy } = usePolicyContext();

  // A specific policy selected → scope to it. "All Policies" → the client's active
  // policies (current administrators), unioned for pinning + the single-primary rule.
  const scoped = useMemo(
    () => (selectedPolicy ? [selectedPolicy] : policies.filter((p) => p.status === PolicyStatus.ACTIVE)),
    [policies, selectedPolicy]
  );
  const singlePolicy = scoped.length === 1 ? scoped[0] : undefined;

  // Carrier company name for the provenance label — how policies are titled across the
  // app (carrier name + year range; see PolicyShow header / PolicySelector), not the aegle id.
  const { data: carrier } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: singlePolicy?.vendor_id_ref ?? "" },
    { enabled: !!singlePolicy?.vendor_id_ref }
  );

  // Memoized so policyVendors keeps a stable identity across renders — otherwise the
  // dialog's downstream memos (roleMeta/pinnedIds/orderedVendors) recompute every render.
  const { defaultVendors, policyVendors, sourceLabel } = useMemo(() => {
    const carrierName = carrier?.company_name || singlePolicy?.source_data?.policy_carrier;
    const policyLabel = singlePolicy
      ? [carrierName, formatYearRange(singlePolicy.effective_start_date, singlePolicy.effective_end_date)]
          .filter(Boolean)
          .join(" ") || "policy"
      : "the client's active policies";

    return {
      // Carrier default (reimbursement/policy path) only when the scope is one policy —
      // don't guess a carrier across multiple either.
      defaultVendors: singlePolicy ? { carrier: singlePolicy.vendor_id_ref } : undefined,
      policyVendors: scoped.length > 0 ? buildPolicyVendorContext(scoped) : undefined,
      // Provenance shown on the prefill: name the policy when the scope is one; make
      // clear it's the client's administrators (a union) otherwise.
      sourceLabel: policyLabel,
    };
  }, [scoped, singlePolicy, carrier]);

  return (
    <UploadTypeDialog
      open={open}
      onClose={onClose}
      defaultVendors={defaultVendors}
      policyVendors={policyVendors}
      defaultSourceLabel={sourceLabel}
      clientIdRef={clientId}
      clientLabel={clientLabel}
    />
  );
};

const ClientShowInner = ({
  drawerExpanded,
  setDrawerExpanded,
  toggleDrawer,
  ClientIcon,
}: {
  drawerExpanded: boolean;
  setDrawerExpanded: (b: boolean) => void;
  toggleDrawer: () => void;
  ClientIcon: React.ElementType;
}) => {
  const record = useRecordContext<Client>();

  // Fetch policies for this client
  const { data: policies = [], isLoading: policiesLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: record?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!record?.id }
  );

  // The policy useGetList is gated on record?.id, so it reports isLoading: false
  // while disabled. Treat "no record yet" the same as "still loading" so derived
  // predicates default to the affordance-available state instead of producing a
  // false negative during the brief window before the record resolves.
  const policyContextReady = !!record?.id && !policiesLoading;

  const hasActivePolicy = !policyContextReady || policies.some((p) => p.status === "active");

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [policyCreateDialogOpen, setPolicyCreateDialogOpen] = useState(false);

  return (
    <PolicyProvider policies={policies} defaultToAll>
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
          marginRight: drawerExpanded ? "500px" : 0,
        }}
      >
        <PageHeader
          titleKey="client_legal_name"
          statusKey="client_status"
          hideSectionTitle
          headerIcon={<ClientIcon sx={{ fontSize: 28, color: "primary.main" }} />}
          rightContent={
            <ClientShowActions
              setDrawerExpanded={setDrawerExpanded}
              drawerExpanded={drawerExpanded}
              onUploadClick={() => setUploadDialogOpen(true)}
              hasActivePolicy={hasActivePolicy}
            />
          }
        />
        <PageContent>
          {!hasActivePolicy && record?.id && (
            <Box mb={2}>
              <Alert
                severity="warning"
                sx={{ alignItems: "center", pr: 4 }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setPolicyCreateDialogOpen(true)}
                  >
                    Create Policy
                  </Button>
                }
              >
                <AlertTitle>No Active Policy</AlertTitle>
                This client does not have an active stop-loss policy. Claims and reimbursements require an active
                policy.
              </Alert>
            </Box>
          )}
          <ClientTabbedShowLayout
            drawerExpanded={drawerExpanded}
            hasPolicies={policies.length > 0}
            policyContextReady={policyContextReady}
          />
        </PageContent>
      </Box>
      <ClientSideDrawer drawerExpanded={drawerExpanded} toggleDrawer={toggleDrawer} />
      <ClientUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        clientId={record?.id}
        clientLabel={record?.client_legal_name}
      />
      <PolicyCreateDialog
        open={policyCreateDialogOpen}
        onClose={() => setPolicyCreateDialogOpen(false)}
        defaultClientId={record?.id}
      />
    </PolicyProvider>
  );
};

const ClientTabbedShowLayout = ({
  drawerExpanded,
  hasPolicies,
  policyContextReady,
}: {
  drawerExpanded: boolean;
  hasPolicies: boolean;
  policyContextReady: boolean;
}) => {
  const clientRecord = useRecordContext<Client>();

  const { client_status } = clientRecord ?? {};

  const showClientStatusAlert = client_status === ClientStatus.BUILDING;

  const { policies } = usePolicyContext();
  // Keep the Aggregate Deductible tab mounted until the policy context is
  // ready so its URL path resolves on first render. Otherwise MUI Tabs warns
  // "value provided to the Tabs component is invalid" when the user navigates
  // straight to /show/aggregate-deductible before policies arrive.
  const hasAggregateDeductible =
    !policyContextReady || policies.some((p) => p.aggregate_benefit?.is_aggregate_deductible);

  // Permissions
  const { canList: canListStoplossClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const { canList: canListClaimItems } = useResourcePermissions(ResourceName.CLAIM_ITEMS);

  // Hide the page-level policy filter on the Claims Summary tab — that tab has its
  // own policy selector, so the top-level "Viewing by" misleads users into thinking
  // it drives the summary's context.
  const location = useLocation();
  const onClaimsSummaryTab = location.pathname.includes("claims-summary");

  return (
    <>
      {showClientStatusAlert && (
        <Box mb={2}>
          <Alert severity={AlertType.INFO}>
            Client is currently in a building status, please set to &quot;Active&quot; when fully setup with a policy.
          </Alert>
        </Box>
      )}
      {/* Tab bar with policy selector inline */}
      <Box sx={{ position: "relative" }}>
        {hasPolicies && !onClaimsSummaryTab && (
          <Box sx={{ position: "absolute", right: 0, top: 8, zIndex: 1 }}>
            <PolicySelector showAllOption label="Filter by policy" />
          </Box>
        )}
      </Box>
      <StyledTabbedShowLayout>
        <Tab label="Dashboard">
          <ClientReportingTab drawerExpanded={drawerExpanded} />
        </Tab>
        <Tab label="Overview" path="overview">
          <ClientOverviewTab drawerExpanded={drawerExpanded} />
        </Tab>
        <Tab label="Claims Summary" path="claims-summary">
          <ClaimsSummaryTab />
        </Tab>
        {canListStoplossClaims && canListClaimItems && (
          <Tab label="Specific Claims" path="claims">
            <ClientClaimsTab />
          </Tab>
        )}
        {featureFlags.aggregateCalculationsEnabled && hasAggregateDeductible && (
          <Tab label="Aggregate Claims" path="aggregate-deductible">
            <AggregateDeductibleTab />
          </Tab>
        )}
        <Tab label="Audit Log" path="audit">
          <AuditLogBox tableName={ResourceName.CLIENTS} />
        </Tab>
      </StyledTabbedShowLayout>
    </>
  );
};

const ClientShowActions = ({
  setDrawerExpanded,
  drawerExpanded,
  onUploadClick,
  hasActivePolicy,
}: {
  setDrawerExpanded: (b: boolean) => void;
  drawerExpanded: boolean;
  onUploadClick: () => void;
  hasActivePolicy: boolean;
}) => {
  const { canDelete } = useResourcePermissions(ResourceName.CLIENTS);
  const { canList: canListTasks } = useResourcePermissions(ResourceName.TASKS);
  const { canCreate: canCreateDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);
  const record = useRecordContext<Client>();

  return (
    <PageHeaderActions
      actions={{
        delete: canDelete,
      }}
    >
      {canCreateDocuments && (
        <Tooltip title={hasActivePolicy ? "Upload Document" : "An active policy is required to upload documents"}>
          <span>
            <IconButton
              size="small"
              onClick={onUploadClick}
              disabled={!hasActivePolicy}
              sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!drawerExpanded && canListTasks && <ViewTasksButton onClick={() => setDrawerExpanded(true)} />}
      <ClientCommentsButton clientId={record?.id ? String(record.id) : undefined} />
    </PageHeaderActions>
  );
};
